import {
    RecommendationData,
    RecommendationResponse,
    RecommendationListResponse,
    RecommendationUpdateParams,
    RecommendationCreationParams,
    RecommendationHistoryQuery,
} from 'chaika-frontend-types/frontend-cms';
import {} from 'chaika-frontend-types/frontend-cms/medical-case';

import { axios } from '@lib/axios';
import { isDefined } from '@utils/isDefined';

const BASE_URL = '/api/recommendation';

export class RecommendationApi {
    public static async findByMedicalCaseIds(medicalCaseIds: string[]): Promise<RecommendationData[]> {
        const res = await axios.get<RecommendationListResponse>(`${BASE_URL}`, {
            params: { medicalCaseId: medicalCaseIds.join(',') },
        });

        return res.data.recommendations;
    }

    public static async findByEhrId(ehrId: string): Promise<RecommendationData[]> {
        const res = await axios.get<RecommendationListResponse>(`${BASE_URL}`, {
            params: { ehrId },
        });

        return res.data.recommendations;
    }

    public static async getHistory(params: RecommendationHistoryQuery): Promise<RecommendationData[]> {
        const res = await axios.get<RecommendationListResponse>(`${BASE_URL}/history`, {
            params,
        });

        return RecommendationApi.filterIncompleteRecommendations(res.data.recommendations);
    }

    public static async getById(recommendationId: string): Promise<RecommendationData> {
        const res = await axios.get<RecommendationResponse>(`${BASE_URL}/${recommendationId}`);

        return res.data.recommendation;
    }

    public static async create(
        medicalCaseId: string | string[],
        recommendation: RecommendationCreationParams,
    ): Promise<void> {
        await axios.post(`${BASE_URL}`, {
            recommendation: {
                ...recommendation,
                medicalCaseIds: Array.isArray(medicalCaseId) ? medicalCaseId : [medicalCaseId],
            },
        });
    }

    public static async update(
        medicalCaseId: string,
        recommendationId: string,
        recommendation: RecommendationUpdateParams,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${recommendationId}`, {
            recommendation,
        });
    }

    public static async updateImportance(
        medicalCaseId: string,
        recommendationId: string,
        isImportant: boolean,
        entryId?: string,
    ): Promise<void> {
        await axios.put(`${BASE_URL}/${recommendationId}/importance`, {
            recommendation: { isImportant, entryId },
        });
    }

    public static async remove(medicalCaseId: string, recommendationId: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${recommendationId}`);
    }

    private static filterIncompleteRecommendations(
        recommendations: (Partial<RecommendationData> | RecommendationData)[],
    ): RecommendationData[] {
        return recommendations.filter(recommendation => RecommendationApi.isCompleteRecommendation(recommendation));
    }

    private static isCompleteRecommendation(
        recommendation: Partial<RecommendationData> | RecommendationData,
    ): recommendation is RecommendationData {
        return (
            isDefined(recommendation.id) &&
            isDefined(recommendation.medicalCaseIds) &&
            isDefined(recommendation.authorId) &&
            isDefined(recommendation.isImportant) &&
            isDefined(recommendation.comment) &&
            isDefined(recommendation.start) &&
            isDefined(recommendation.createdAt)
        );
    }
}
